import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { rewardsAPI } from '../services/api';
import '../styles/Rewards.css';

export default function Rewards() {
  const [streak, setStreak] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [history, setHistory] = useState([]);
  const [bonusSummary, setBonusSummary] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [claimResult, setClaimResult] = useState(null);
  const [tab, setTab] = useState('daily');
  const [loading, setLoading] = useState(true);
  const [claimBurstActive, setClaimBurstActive] = useState(false);
  const burstTimerRef = useRef(null);
  const audioCtxRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [streakRes, achRes, histRes, bonusRes] = await Promise.all([
        rewardsAPI.getStreak(),
        rewardsAPI.getAchievements(),
        rewardsAPI.getHistory(),
        rewardsAPI.getBonusSummary().catch(() => ({ data: null })),
      ]);
      setStreak(streakRes.data);
      setAchievements(achRes.data);
      setHistory(histRes.data);
      if (bonusRes?.data) setBonusSummary(bonusRes.data);
    } catch {
      // ignore
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Hide any achievement or bonus reward greater than 100 WKP (retained in DB for future use)
  const visibleAchievements = useMemo(() => {
    return achievements.filter((a) => Number(a.reward_amount || a.reward || 0) <= 100);
  }, [achievements]);

  const visibleVaultRewards = useMemo(() => {
    if (!bonusSummary?.rewards) return [];
    return bonusSummary.rewards.filter((r) => Number(r.amount || 0) <= 100);
  }, [bonusSummary]);

  useEffect(() => () => {
    if (burstTimerRef.current) {
      window.clearTimeout(burstTimerRef.current);
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
  }, []);

  const ensureAudioContext = () => {
    if (typeof window === 'undefined') return null;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return null;
    if (!audioCtxRef.current) {
      audioCtxRef.current = new AudioCtx();
    }
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume().catch(() => {});
    }
    return audioCtxRef.current;
  };

  const playClaimSound = () => {
    try {
      const ctx = ensureAudioContext();
      if (!ctx) return;
      const now = ctx.currentTime;
      const master = ctx.createGain();
      const hit = ctx.createOscillator();
      const shimmer = ctx.createOscillator();
      const filter = ctx.createBiquadFilter();

      hit.type = 'triangle';
      shimmer.type = 'sine';
      hit.frequency.setValueAtTime(860, now);
      hit.frequency.exponentialRampToValueAtTime(470, now + 0.12);
      shimmer.frequency.setValueAtTime(1320, now + 0.01);
      shimmer.frequency.exponentialRampToValueAtTime(920, now + 0.09);

      filter.type = 'bandpass';
      filter.frequency.setValueAtTime(1150, now);
      filter.Q.setValueAtTime(1.1, now);

      master.gain.setValueAtTime(0.0001, now);
      master.gain.exponentialRampToValueAtTime(0.18, now + 0.01);
      master.gain.exponentialRampToValueAtTime(0.05, now + 0.08);
      master.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);

      hit.connect(filter);
      shimmer.connect(filter);
      filter.connect(master);
      master.connect(ctx.destination);

      hit.start(now);
      shimmer.start(now + 0.012);
      hit.stop(now + 0.18);
      shimmer.stop(now + 0.14);
    } catch {
      // ignore sound failures
    }
  };

  const triggerClaimBurst = () => {
    setClaimBurstActive(false);
    if (burstTimerRef.current) {
      window.clearTimeout(burstTimerRef.current);
    }
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => setClaimBurstActive(true));
      burstTimerRef.current = window.setTimeout(() => {
        setClaimBurstActive(false);
        burstTimerRef.current = null;
      }, 700);
      return;
    }
    setClaimBurstActive(true);
  };

  const handleClaim = async () => {
    playClaimSound();
    triggerClaimBurst();
    setClaiming(true);
    setClaimResult(null);
    try {
      const res = await rewardsAPI.claimDaily();
      setClaimResult(res.data);
      load(); // refresh
    } catch {
      setClaimResult({ error: true });
    }
    setClaiming(false);
  };

  if (loading) return <div className="rewards-page"><p className="rewards-loading">Loading...</p></div>;

  const unlockedCount = achievements.filter((a) => a.unlocked).length;
  const categories = Array.from(new Set(achievements.map((a) => a.category).filter(Boolean)));

  return (
    <div className="rewards-page">
      <h1 className="rewards-title">Rewards & Achievements</h1>

      <div className="rewards-tabs">
        <button className={`rewards-tab ${tab === 'daily' ? 'active' : ''}`} onClick={() => setTab('daily')}>
          🔥 Daily Rewards
        </button>
        <button className={`rewards-tab ${tab === 'vault' ? 'active' : ''}`} onClick={() => setTab('vault')}>
          🎁 Bonus Vault {bonusSummary?.lockedBonusTotal > 0 ? `(🔒 ${bonusSummary.lockedBonusTotal} WP)` : ''}
        </button>
        <button className={`rewards-tab ${tab === 'badges' ? 'active' : ''}`} onClick={() => setTab('badges')}>
          🏆 Badges ({unlockedCount}/{achievements.length})
        </button>
        <button className={`rewards-tab ${tab === 'history' ? 'active' : ''}`} onClick={() => setTab('history')}>
          📜 History
        </button>
      </div>

      {tab === 'daily' && (
        <div className="rewards-daily">
          <div className="streak-card">
            <div className="streak-flame">🔥</div>
            <div className="streak-value">{streak?.currentStreak || 0}</div>
            <div className="streak-label">Day Streak</div>
            <div className="streak-best">Best: {streak?.longestStreak || 0} days</div>
          </div>

          {/* 7-day cycle grid */}
          <div className="cycle-info">
            <span className="cycle-tag">
              {streak?.completedCycles >= 1
                ? `Cycle ${(streak?.completedCycles || 0) + 1}`
                : 'First Cycle'}
            </span>
            <span className="cycle-reward-tag">
              🎁 {streak?.nextCycleReward || 30} WKP on Day 7
            </span>
          </div>

          <div className="reward-schedule reward-schedule-7">
            {[1, 2, 3, 4, 5, 6, 7].map((day) => {
              const cycleDay = streak?.cycleDay || 0;
              const claimedToday = streak?.claimedToday || false;
              // A day is completed if it's before the current cycle position
              const isCompleted = claimedToday ? day <= cycleDay : day < cycleDay;
              // Current day to claim
              const isCurrent = !claimedToday && ((cycleDay + 1 === day) || (cycleDay === 0 && day === 1));
              const isPayday = day === 7;
              return (
                <div
                  key={day}
                  className={`reward-day ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''} ${isPayday ? 'payday' : ''}`}
                >
                  <div className="reward-day-num">Day {day}</div>
                  <div className="reward-day-icon">
                    {isCompleted ? '✅' : isCurrent ? '📍' : isPayday ? '🎁' : '🔒'}
                  </div>
                  {isPayday && (
                    <div className="reward-day-amt">{streak?.nextCycleReward || 30} WKP</div>
                  )}
                </div>
              );
            })}
          </div>

          <p className="cycle-hint">
            Login every day for 7 days to earn your reward. Miss a day and it resets!
          </p>

          <div className={`claim-burst ${claimBurstActive ? 'active' : ''}`} aria-hidden="true">
            <span className="claim-burst__ring claim-burst__ring--one" />
            <span className="claim-burst__ring claim-burst__ring--two" />
            <span className="claim-burst__spark claim-burst__spark--one" />
            <span className="claim-burst__spark claim-burst__spark--two" />
            <span className="claim-burst__spark claim-burst__spark--three" />
            <span className="claim-burst__spark claim-burst__spark--four" />
          </div>

          {!streak?.claimedToday ? (
            <button className="claim-btn" onClick={handleClaim} disabled={claiming}>
              {claiming ? 'Checking in...' : '📍 Check In Today'}
            </button>
          ) : (
            <div className="claimed-msg">✅ Checked in today! Come back tomorrow.</div>
          )}

          {claimResult && !claimResult.error && claimResult.claimed && claimResult.reward > 0 && (
            <div className="claim-success">
              🎉 You completed a 7-day streak and earned <strong>{claimResult.reward} WKP</strong> (added to your Bonus Vault)!
              {claimResult.newAchievements?.length > 0 && (
                <div className="new-achievements">
                  {claimResult.newAchievements.map((a) => (
                    <span key={a.key} className="new-ach-badge">{a.icon} {a.name}{a.reward > 0 ? ` (+${a.reward} WP)` : ''}</span>
                  ))}
                </div>
              )}
            </div>
          )}

          {claimResult && !claimResult.error && claimResult.claimed && claimResult.reward === 0 && (
            <div className="claim-checkin">
              ✅ Day {claimResult.streak} checked! {7 - (claimResult.streak % 7 || 7)} days left until your reward.
              {claimResult.newAchievements?.length > 0 && (
                <div className="new-achievements">
                  {claimResult.newAchievements.map((a) => (
                    <span key={a.key} className="new-ach-badge">{a.icon} {a.name}{a.reward > 0 ? ` (+${a.reward} WP)` : ''}</span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'vault' && (
        <div className="rewards-vault">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 24 }}>
            <div style={{ padding: 20, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 16, textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 1 }}>Unlocked Bonus Balance</div>
              <div style={{ fontSize: 32, fontWeight: 900, color: '#10b981', margin: '8px 0' }}>{bonusSummary?.bonusBalance || 0} WKP</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>Available for games</div>
            </div>
            <div style={{ padding: 20, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 16, textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 1 }}>Locked Pending Rewards</div>
              <div style={{ fontSize: 32, fontWeight: 900, color: '#f59e0b', margin: '8px 0' }}>🔒 {bonusSummary?.lockedBonusTotal || 0} WKP</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>Expires in 3 days each</div>
            </div>
          </div>

          {bonusSummary?.lockedBonusTotal > 0 && (
            <div style={{ padding: 16, background: 'linear-gradient(135deg, rgba(225,29,72,0.2), rgba(15,23,42,0.9))', border: '1px solid #e11d48', borderRadius: 14, marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 15, color: '#fff' }}>🔒 You have {bonusSummary.lockedBonusTotal} WKP locked bonus rewards!</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginTop: 4 }}>Make any deposit to instantly unlock and claim all your pending bonus rewards. Unclaimed bonuses expire in 3 days!</div>
              </div>
              <a href="/ledger" style={{ padding: '10px 20px', background: '#e11d48', color: '#fff', borderRadius: 10, fontWeight: 800, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                💳 Deposit Now to Unlock
              </a>
            </div>
          )}

          <h3 style={{ fontSize: 16, fontWeight: 800, marginBottom: 12 }}>Reward Vault History & Pending</h3>
          {(visibleVaultRewards.length === 0) ? (
            <p className="no-history">No bonus rewards in vault yet. Claim daily rewards to earn bonuses!</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {visibleVaultRewards.map((r) => {
                const hoursLeft = Math.floor(r.expiresInSeconds / 3600);
                const minsLeft = Math.floor((r.expiresInSeconds % 3600) / 60);
                const isLocked = r.status === 'locked';
                const isUnlocked = r.status === 'unlocked';
                const isExpired = r.status === 'expired';

                return (
                  <div key={r.id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '14px 18px', background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12,
                    opacity: isExpired ? 0.5 : 1
                  }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>
                        {r.description || r.rewardType}
                        <span style={{
                          marginLeft: 10, fontSize: 10, padding: '2px 8px', borderRadius: 4,
                          textTransform: 'uppercase', fontWeight: 800,
                          background: isUnlocked ? '#10b981' : isLocked ? '#f59e0b' : '#64748b',
                          color: '#fff'
                        }}>
                          {isUnlocked ? '✅ UNLOCKED' : isLocked ? '🔒 LOCKED' : '❌ EXPIRED'}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                        Granted: {new Date(r.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 900, fontSize: 16, color: isUnlocked ? '#10b981' : isLocked ? '#f59e0b' : '#94a3b8' }}>
                        +{r.amount} WKP
                      </div>
                      {isLocked && (
                        <div style={{ fontSize: 11, color: '#fbbf24', marginTop: 2, fontWeight: 700 }}>
                          ⏱️ Expires in {hoursLeft}h {minsLeft}m
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === 'badges' && (
        <div className="rewards-badges">
          {categories.map((cat) => {
            const catAch = visibleAchievements.filter((a) => a.category === cat);
            if (!catAch.length) return null;
            return (
              <div key={cat} className="badge-category">
                <h3 className="badge-cat-title">{cat.charAt(0).toUpperCase() + cat.slice(1)}</h3>
                <div className="badge-grid">
                  {catAch.map((a) => (
                    <div key={a.id} className={`badge-card ${a.unlocked ? 'unlocked' : 'locked'}`}>
                      <div className="badge-icon">{a.icon}</div>
                      <div className="badge-name">{a.name}</div>
                      <div className="badge-desc">{a.description}</div>
                      {a.reward_amount > 0 && <div className="badge-reward">+{a.reward_amount} WP</div>}
                      {a.unlocked && a.unlocked_at && (
                        <div className="badge-date">{new Date(a.unlocked_at).toLocaleDateString()}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === 'history' && (
        <div className="rewards-history">
          {history.length === 0 ? (
            <p className="no-history">No reward history yet. Claim your first daily reward!</p>
          ) : (
            <table className="history-table">
              <thead>
                <tr><th>Date</th><th>Day</th><th>Reward</th></tr>
              </thead>
              <tbody>
                {history.map((h, i) => (
                  <tr key={i}>
                    <td>{new Date(h.login_date).toLocaleDateString()}</td>
                    <td>Day {h.streak_day}</td>
                    <td>+{h.reward_amount} WP</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
